import CryptoJS from "crypto-js";
import { RELEASE_NOTES_SEED } from "./releaseNotesData";

/**
 * Release Notes & Roadmap — data access layer.
 *
 * Single seam between the UI and the backend `release-notes` Azure Function:
 *   GET  {base}                   -> encrypted { releases, upcoming }
 *   GET  {base}&type=comments     -> encrypted Comment[]
 *   POST {base}&type=comments     -> saved Comment (plain JSON)
 *
 * When REACT_APP_RELEASE_NOTES_API is set the network path is used (responses
 * are AES-encrypted like the app's other endpoints and decrypted here). If the
 * env var is unset or a request fails, it falls back to bundled seed content +
 * localStorage so the page keeps working — nothing the user does is lost.
 */

const SECRET_KEY = "1234567890abcdef1234567890abcdef";
const LS_COMMENTS_KEY = "releaseNotesComments";

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };
};

// The base URL already carries the function key (?code=...), so extra params
// are appended with & / ? exactly like the app's other service helpers.
const withParams = (baseUrl, params) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  if (!qs) return baseUrl;
  return baseUrl.includes("?") ? `${baseUrl}&${qs}` : `${baseUrl}?${qs}`;
};

const decrypt = (ciphertext) => {
  const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch the releases + upcoming backlog items.
 * @returns {Promise<{releases: Array, upcoming: Array}>}
 */
export async function getReleaseNotes() {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;

  if (baseUrl) {
    try {
      const res = await fetch(baseUrl, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = decrypt(await res.text());
      return {
        releases: Array.isArray(data?.releases) ? data.releases : [],
        upcoming: Array.isArray(data?.upcoming) ? data.upcoming : [],
        // Whether this user may edit the roadmap. Decided by the backend from
        // the JWT; used only to show/hide controls — every write is re-checked
        // server-side, so a tampered flag gains nothing.
        isAdmin: data?.isAdmin === true,
        // Who owns this: requests go to them, they approve, they post updates.
        owner: data?.owner || null,
        // Release-note permissions. Which controls to render only — the
        // server re-checks every write.
        canAuthorReleases: data?.canAuthorReleases === true,
        canApproveReleases: data?.canApproveReleases === true,
      };
    } catch (err) {
      console.error("getReleaseNotes error, falling back to seed:", err);
      // Fall through to seed so the page still renders.
    }
  }

  await delay(150);
  return {
    releases: RELEASE_NOTES_SEED.releases,
    upcoming: RELEASE_NOTES_SEED.upcoming,
    isAdmin: false,
    owner: null,
    canAuthorReleases: false,
    canApproveReleases: false,
  };
}

/* ── Release-note authoring ─────────────────────────────────────────────
   Writing a release note is a product action rather than a database one.
   Two permissions: authors draft and submit, approvers publish or send
   back. Every call below is re-authorised server-side. */

/**
 * Release notes not yet published (Draft + In review).
 * Authors and approvers only — 403 for anyone else, which is expected.
 * @returns {Promise<Array>}
 */
export async function getReleaseDrafts() {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) return [];
  try {
    const res = await fetch(withParams(baseUrl, { type: "drafts" }), {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return []; // 403 for non-authors is expected, not an error
    const data = decrypt(await res.text());
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("getReleaseDrafts error:", err);
    return [];
  }
}

const postJson = async (type, payload) => {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) throw new Error("Release Notes API is not configured");

  const res = await fetch(withParams(baseUrl, { type }), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message || `Status ${res.status}`);
  }
  return await res.json();
};

/**
 * Create or update a draft release note. Authors only.
 * A published note is rejected with a 409 — approved text and live text have
 * to stay the same text.
 */
export async function saveReleaseDraft(draft) {
  return postJson("release-save", draft);
}

/** Send a draft for approval. Emails the approvers. Authors only. */
export async function submitReleaseDraft(code) {
  return postJson("release-submit", { code });
}

/**
 * Publish a note, or send it back with a reason. Approvers only, and never
 * for a note you wrote yourself.
 * @param {{code:string, approve:boolean, note?:string, approver?:string}} d
 */
export async function reviewReleaseDraft(decision) {
  return postJson("release-review", decision);
}

/**
 * Questions raised against roadmap items, for the client to answer.
 * @returns {Promise<Array>}
 */
export async function getReleaseQuestions() {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) return [];
  try {
    const res = await fetch(withParams(baseUrl, { type: "questions" }), {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    const data = decrypt(await res.text());
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("getReleaseQuestions error:", err);
    return [];
  }
}

/**
 * Feature requests awaiting review. Admin only — the server returns 403 to
 * anyone else, since an unreviewed request shouldn't read as roadmap work.
 * @returns {Promise<Array>}
 */
export async function getPendingRequests() {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) return [];
  try {
    const res = await fetch(withParams(baseUrl, { type: "pending" }), {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return []; // 403 for non-admins is expected, not an error
    const data = decrypt(await res.text());
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("getPendingRequests error:", err);
    return [];
  }
}

/**
 * Approve a request onto the roadmap, or decline it. Admin only.
 * @param {{itemId:string, approve:boolean}} decision
 */
export async function reviewFeatureRequest({ itemId, approve }) {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) throw new Error("Release Notes API is not configured");

  const res = await fetch(withParams(baseUrl, { type: "request-review" }), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ itemId, approve }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message || `Status ${res.status}`);
  }
  return await res.json();
}

/**
 * Move a roadmap item's stage and/or target date. Admin only — the server
 * returns 403 for anyone else.
 *
 * The stage IS the status update. There is no percentage and no per-item
 * checklist: both were removed as more precision than we can honour.
 * @param {{itemId:string, status?:string, eta?:string}} patch
 */
export async function updateRoadmapItem(patch) {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) throw new Error("Release Notes API is not configured");

  const res = await fetch(withParams(baseUrl, { type: "backlog-update" }), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message || `Status ${res.status}`);
  }
  return await res.json();
}

/** Raise a question against an item. Admin only. */
export async function createReleaseQuestion({ itemId, text }) {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) throw new Error("Release Notes API is not configured");

  const res = await fetch(withParams(baseUrl, { type: "questions" }), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ itemId, text }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message || `Status ${res.status}`);
  }
  return await res.json();
}

/** Answer an open question. Open to any signed-in user. */
export async function answerReleaseQuestion({ questionId, text, author }) {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;
  if (!baseUrl) throw new Error("Release Notes API is not configured");

  const res = await fetch(withParams(baseUrl, { type: "answer" }), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ questionId, text, author }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message || `Status ${res.status}`);
  }
  return await res.json();
}

/**
 * Read all comments (across every item).
 * @returns {Promise<Array>} Comment[]  (each keyed to an item via `itemId`)
 */
export async function getReleaseComments() {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;

  if (baseUrl) {
    try {
      const res = await fetch(withParams(baseUrl, { type: "comments" }), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = decrypt(await res.text());
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("getReleaseComments error, falling back to local:", err);
    }
  }

  return readLocalComments();
}

/**
 * Fetch the list of people a comment can be directed at (from AdminDetails).
 * This is the whole set of choices — a comment must name one of them.
 * @returns {Promise<Array<{id:number,name:string,email:string,role:string}>>}
 */
export async function getShareAudiences() {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;

  if (baseUrl) {
    try {
      const res = await fetch(withParams(baseUrl, { type: "audiences" }), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = decrypt(await res.text());
      return dedupeByName(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("getShareAudiences error, falling back:", err);
    }
  }

  return [];
}

// AdminDetails can contain duplicate people; collapse to one entry per name
// (case-insensitive) so the "Share with" dropdown stays clean.
function dedupeByName(list) {
  const seen = new Set();
  const out = [];
  for (const a of list) {
    const key = (a?.name || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/**
 * Create a new feature request / backlog item (client-raised requirement).
 * It enters the roadmap at the "Exploring" stage.
 * @param {Object} req
 * @param {string} req.title
 * @param {string} [req.description]
 * @param {string} [req.eta]
 * @returns {Promise<Object>} the persisted BacklogItem (id = ItemKey)
 */
export async function createFeatureRequest(req) {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;

  const payload = {
    title: (req.title || "").trim(),
    description: (req.description || "").trim(),
    eta: (req.eta || "").trim(),
  };

  if (baseUrl) {
    const res = await fetch(withParams(baseUrl, { type: "backlog" }), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return await res.json();
  }

  // No backend configured — return an optimistic item so the UI still updates.
  return {
    id: `local-req-${Date.now()}`,
    title: payload.title,
    description: payload.description,
    status: "Exploring",
    eta: payload.eta,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Add a comment to an item.
 * @param {Object} comment
 * @param {string} comment.itemId    - release / backlog id the comment is on
 * @param {string} comment.text
 * @param {string} [comment.sharedWith] - audience the comment is directed at
 * @param {string} comment.author
 * @param {string} [comment.role]
 * @returns {Promise<Object>} the persisted Comment (with id + createdAt)
 */
export async function addReleaseComment(comment) {
  const baseUrl = process.env.REACT_APP_RELEASE_NOTES_API;

  const payload = {
    itemId: comment.itemId,
    text: (comment.text || "").trim(),
    // Always a named person now — the "Everyone" option was removed because it
    // implied a broadcast the notification never actually performed.
    sharedWith: comment.sharedWith || "",
    // AdminID of the person the comment is directed at. The backend resolves
    // their mailbox from this rather than from the display name, which isn't
    // unique.
    sharedWithId: comment.sharedWithId ?? null,
    author: comment.author || "User",
    role: comment.role || "",
  };

  if (baseUrl) {
    try {
      const res = await fetch(withParams(baseUrl, { type: "comments" }), {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error("addReleaseComment error, saving locally:", err);
      // Fall through to local persistence so the comment isn't lost.
    }
  }

  const saved = {
    ...payload,
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
  };
  const all = readLocalComments();
  all.push(saved);
  writeLocalComments(all);
  return saved;
}

// ── localStorage helpers (fallback persistence) ──────────────────────────

function readLocalComments() {
  try {
    const raw = localStorage.getItem(LS_COMMENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error("readLocalComments parse error:", err);
    return [];
  }
}

function writeLocalComments(comments) {
  try {
    localStorage.setItem(LS_COMMENTS_KEY, JSON.stringify(comments));
  } catch (err) {
    console.error("writeLocalComments error:", err);
  }
}
